import * as yup from 'yup'
import type { GenericObject, SubmissionHandler } from 'vee-validate'
import type { Alert, EmailAlertContent } from '~/types/models/alert'

export interface AlertActionResult {
  ok: boolean
  message?: string
}

export const ALERT_SAFE_MESSAGES: ReadonlySet<string> = new Set<string>([
  'Please select at least one topic to subscribe to.',
  'Please select at least one topic to unsubscribe from.',
  'Unable to subscribe. Please try again later.',
  'Unable to unsubscribe. Please try again later.',
])

export const useKaleidoscopeAlerts = () => {
  const { addEmailAlert, deleteEmailAlert } = useKaleidoscopeApi()

  const schema = yup.object({
    email: yup.string().required().email().label('Email'),
    topic: yup
      .array()
      .label('Alert Type')
      .of(yup.string<EmailAlertContent>().required())
      .min(1, 'Pick at least one topic')
      .required(),
  })

  const onAddEmailAlerts: SubmissionHandler<GenericObject> = async (values, { resetForm }) => {
    const { topic, email } = values as Alert.FormValues

    if (!topic?.length) {
      throw new Error('Please select at least one topic to subscribe to.')
    }

    try {
      for (const content of [...new Set(topic)]) {
        await addEmailAlert(email, content)
      }
      resetForm()
    }
    catch (ex) {
      if (ex instanceof Error && ex.message.startsWith('Please select')) {
        throw ex
      }
      throw new Error('Unable to subscribe. Please try again later.')
    }
  }

  const onDeleteEmailAlerts: SubmissionHandler<GenericObject> = async (values, { resetForm }) => {
    const { topic, email } = values as Alert.FormValues

    if (!topic?.length) {
      throw new Error('Please select at least one topic to unsubscribe from.')
    }

    try {
      for (const content of [...new Set(topic)]) {
        await deleteEmailAlert(email, content)
      }
      resetForm()
    }
    catch (ex) {
      if (ex instanceof Error && ex.message.startsWith('Please select')) {
        throw ex
      }
      throw new Error('Unable to unsubscribe. Please try again later.')
    }
  }

  return {
    schema,
    onAddEmailAlerts,
    onDeleteEmailAlerts,
  }
}
